import { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, DownloadIcon } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { getGuide, getRelatedGuides } from '../../data/referenceGuides';
import { BlockRenderer } from '../../components/reference/blocks';
import { ReferenceGuideTOCMobile, ReferenceGuideTOCRail } from '../../components/reference/ReferenceGuideTOC';
import { useSectionSpy } from '../../components/reference/useSectionSpy';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { CTABanner } from '../../components/CTABanner';
const ACCENT_TEXT = {
  inventory: 'text-guide-inventory',
  manufacturing: 'text-guide-manufacturing',
  procurement: 'text-guide-procurement'
} as const;
export function ReferenceGuidePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    hash: sectionHash,
    pathname
  } = useLocation();
  const guide = getGuide(slug);
  const posthog = usePostHog();
  const sectionIds = useMemo(() => guide?.sections.map(s => s.id) ?? [], [guide]);
  const activeId = useSectionSpy({
    sectionIds,
    onSectionViewed: sectionId => {
      if (guide) posthog?.capture('reference_guide_section_viewed', {
        slug: guide.slug,
        section_id: sectionId
      });
    }
  });
  useDocumentMeta({
    title: guide ? `${guide.title} | Reference Guide | Thruline` : 'Guide not found | Thruline',
    description: guide?.description,
    noindex: !guide
  });

  /**
   * Hash-aware scroll restoration.
   *
   * CaseStudiesPage.tsx:48 and SolutionPageLayout.tsx:54 both run an
   * unconditional window.scrollTo(0, 0) on mount. Copying that here would race
   * every deep link: a reader arriving at .../procurement-systems#pr-vs-po
   * would be yanked to the top before the browser could reach the anchor.
   * So: honour the hash when there is one, scroll to top only when there isn't.
   */
  useEffect(() => {
    const anchor = sectionHash.replace(/^#/, '');
    const target = anchor ? document.getElementById(anchor) : null;
    if (target) {
      target.scrollIntoView({
        block: 'start'
      });
      return;
    }
    window.scrollTo(0, 0);
  }, [sectionHash, slug]);
  useEffect(() => {
    if (guide) {
      posthog?.capture('reference_guide_viewed', {
        slug: guide.slug,
        referrer: document.referrer || null
      });
    }
  }, [guide, posthog]);
  if (!guide) {
    // A client-side route cannot set an HTTP status, so the honest move is to
    // tell crawlers not to index this. useDocumentMeta appends the robots tag
    // to <head> above — React 18 leaves a JSX <meta> stranded in the body,
    // where it means nothing. A true 404 needs a host route manifest, which is
    // Phase B, section 7 of the plan.
    return <div className="min-h-screen bg-navy text-white pt-32 pb-20 flex items-center justify-center px-6" data-id="element-1183" data-mp="249lkn4sccgi7">
        <div className="text-center" data-id="element-1184" data-mp="fsam63nku5y9">
          <h1 className="text-3xl font-bold mb-4" data-id="element-1185" data-mp="ftby0acf2wh1">Reference guide not found</h1>
          <p className="text-white/55 mb-8" data-id="element-1186" data-mp="jte20q5mk1zh">
            That guide doesn’t exist, or the link has changed.
          </p>
          <Link to="/resources/reference-guides" className="inline-flex items-center gap-2 text-violet-link hover:underline" data-id="element-1187" data-mp="orxm0l0nr3p7">
            Browse all reference guides
            <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-1188" data-mp="yj20ljmf8mji" />
          </Link>
        </div>
      </div>;
  }
  const related = getRelatedGuides(guide.slug);
  const accent = ACCENT_TEXT[guide.accent];
  return <div className="min-h-screen bg-navy text-white pt-32 pb-20" data-id="element-1189" data-mp="17iymjdhff7av">
      <a href={`#${pathname}#guide-content`} onClick={event => {
      event.preventDefault();
      const content = document.getElementById('guide-content');
      content?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      content?.focus({
        preventScroll: true
      });
    }} className="sr-only focus:not-sr-only focus:fixed focus:top-20 focus:left-6 focus:z-50 focus:px-5 focus:py-3 focus:rounded-full focus:bg-white focus:text-navy focus:font-semibold focus:text-sm" data-id="element-1190" data-mp="17hnhlac8vti6">
        Skip to content
      </a>

      <div className="max-w-6xl mx-auto px-6" data-id="element-1191" data-mp="2sww41tgem6ns">
        <nav aria-label="Breadcrumb" className="mb-8" data-id="element-1192" data-mp="2svl2m0aflazu">
          <ol className="flex flex-wrap items-center gap-2 text-sm list-none" data-id="element-1193" data-mp="2nzvw1czzutu1">
            <li data-id="element-1194" data-mp="208gs5fskk4go">
              <Link to="/resources/reference-guides" className="text-white/50 hover:text-white" data-id="element-1195" data-mp="20abb4oka5mi4">
                Reference Guides
              </Link>
            </li>
            <li aria-hidden="true" className="text-white/25" data-id="element-1196" data-mp="29ytcp80dvxmt">
              /
            </li>
            <li className="text-white/70" data-id="element-1197" data-mp="258movadp4h5m">{guide.title}</li>
          </ol>
        </nav>

        <motion.header initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="max-w-3xl mb-14" data-id="element-1198" data-mp="1hisdkx90z0nh">
          <div className="flex items-center gap-3 mb-4" data-id="element-1199" data-mp="3o70ze36oq6c6">
            <span className={`${accent} text-xs font-semibold tracking-widest uppercase`} data-id="element-1200" data-mp="3o5gaat353flp">
              Reference Guide
            </span>
            <span aria-hidden="true" className="text-white/25" data-id="element-1201" data-mp="3e8uynowm768w">
              •
            </span>
            <span className="text-white/50 text-sm" data-id="element-1202" data-mp="3j7xz0w1ngoaj">{guide.category}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.1] tracking-tight mb-6" data-id="element-1203" data-mp="as4rh6paqn5m">
            {guide.title}
          </h1>
          <p className="text-lg text-white/60 leading-relaxed mb-8" data-id="element-1204" data-mp="fpwky55lahbx">{guide.subtitle}</p>

          <div className="flex flex-wrap items-center gap-4" data-id="element-1205" data-mp="1qer3bdfsdvp">
            {/* The packet is gated: we take the request, then email the link.
                The S3 objects behind guide.pdf are not publicly readable. */}
            <Link to={`/contact?packet=${guide.slug}`} onClick={() => posthog?.capture('reference_guide_pdf_requested', {
            slug: guide.slug
          })} className="inline-flex items-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-colors" data-id="element-1206" data-mp="2mg45jna89xnv">
              <DownloadIcon aria-hidden="true" className="w-4 h-4" data-id="element-1207" data-mp="s3t74zzqllzq" />
              Download the PDF packet
            </Link>
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" target="_blank" rel="noopener noreferrer" onClick={() => posthog?.capture('reference_guide_call_booked', {
            slug: guide.slug
          })} className="inline-flex items-center min-h-[44px] px-6 py-3 rounded-full border border-white/25 text-white font-medium text-sm hover:border-white/50 transition-colors" data-id="element-1208" data-mp="ibxaltdau3xm">
              Talk to us about your stack
            </a>
            <span className="text-white/50 text-sm sm:ml-auto" data-id="element-1209" data-mp="3vk08v0yld95v">{guide.readMinutes} min read</span>
          </div>
        </motion.header>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-12" data-id="element-1210" data-mp="1tr2cww9iq0gp">
          <aside className="hidden lg:block" data-id="element-1211" data-mp="1toq0eymj4ib1">
            <ReferenceGuideTOCRail sections={guide.sections} activeId={activeId} data-id="element-1212" data-mp="228mhbehb5wge" />
          </aside>

          <div className="min-w-0" data-id="element-1213" data-mp="268mz993m8ps7">
            <ReferenceGuideTOCMobile sections={guide.sections} activeId={activeId} data-id="element-1214" data-mp="267lmn06ybv8d" />

            <main id="guide-content" tabIndex={-1} className="space-y-20 pt-8 lg:pt-0 outline-none" data-id="element-1215" data-mp="2b0oen3w2ywmg">
              {guide.sections.map(section => <section key={section.id} id={section.id} tabIndex={-1} className="outline-none" data-id="element-1216" data-mp="2g12gu5b03cck">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-1217" data-mp="1jbbgvqjxwwzd">
                    {section.heading}
                  </h2>
                  {section.standfirst && <p className="text-white/55 text-base leading-relaxed mb-8 max-w-2xl" data-id="element-1218" data-mp="1s82e46a8ahvj">
                      {section.standfirst}
                    </p>}
                  <div className="space-y-6" data-id="element-1219" data-mp="33pbu2iu5ers3">
                    {section.blocks.map((block, i) => <BlockRenderer key={`${section.id}-${block.kind}-${i}`} block={block} sectionHeading={section.heading} onSolutionClick={to => posthog?.capture('reference_guide_solution_clicked', {
                  slug: guide.slug,
                  target: to
                })} data-id="element-1220" data-mp="38nwtwh5hn9g0" data-mpi={i} />)}
                  </div>
                </section>)}

              {related.length > 0 && <section aria-labelledby="continue-reading" data-id="element-1221" data-mp="7dxv70fi5gaf">
                  <h2 id="continue-reading" className="text-white/50 text-xs font-bold uppercase tracking-widest mb-5" data-id="element-1222" data-mp="2leehuliq51e">
                    Continue reading
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1223" data-mp="cbpuifih84on">
                    {related.map(r => <Link key={r.slug} to={`/resources/reference-guides/${r.slug}`} className="group rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 hover:border-violet-link/30 hover:bg-white/[0.04] transition-colors" data-id="element-1224" data-mp="qb8o83ajjdhf">
                        <span className={`${ACCENT_TEXT[r.accent]} text-xs font-semibold tracking-widest uppercase`} data-id="element-1225" data-mp="2s3n5r196afxb">
                          {r.category}
                        </span>
                        <p className="text-white font-semibold mt-2 mb-2 group-hover:text-violet-link transition-colors" data-id="element-1226" data-mp="12kygvx9jts0r">
                          {r.title}
                        </p>
                        <p className="text-white/55 text-sm leading-relaxed" data-id="element-1227" data-mp="tteijc8tpk6b">{r.question}</p>
                      </Link>)}
                  </div>
                </section>}
            </main>
          </div>
        </div>
      </div>

      <div className="mt-24" data-id="element-1228" data-mp="jtxgd4ss5of3">
        <CTABanner headline={guide.cta?.headline} buttonLabel={guide.cta?.button} onCtaClick={() => posthog?.capture('reference_guide_cta_clicked', {
        slug: guide.slug
      })} data-id="element-1229" data-mp="jtnqdh7qjh9e" />
      </div>
    </div>;
}